'use client';

import React, { useRef, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '@/context/LanguageContext';

const commodities = [
{ image: "/assets/images/rocket_gen_img_10c75a36d-1773234368179.webp", alt: 'Fresh fish and seafood catch with animal protein products in industrial facility', tagKey: 'Mar', nameKey: 'commodities.5.name', catKey: 'Principal' },
{ image: "/assets/images/cordero-patagonico.webp", alt: 'Flock of sheep raised on open Patagonian grassland', tagKey: 'Proteínas', nameKey: 'commodities.7.name', catKey: 'Principal' },
{ image: "/assets/images/proteinas-animales.webp", alt: 'Butcher in white coat and gloves holding a freshly cut beef steak', tagKey: 'Proteínas', nameKey: 'commodities.8.name', catKey: 'Principal' },
{ image: "/assets/images/rocket_gen_img_126ca760f-1772104392217.webp", alt: 'Soybean field with green pods ready for harvest in agricultural landscape', tagKey: 'Granos y Aceites', nameKey: 'commodities.0.name', catKey: 'Principal' },
{ image: "/assets/images/unsplash-1677125683463.webp", alt: 'Coffee beans, black tea leaves and yerba mate', tagKey: 'Infusiones', nameKey: 'commodities.4.name', catKey: 'Principal' },
{ image: "/assets/images/rocket_gen_img_1e088f9c7-1772541951004.webp", alt: 'Raw sugar cane stalks and refined sugar crystals in industrial setting', tagKey: 'Azúcar', nameKey: 'commodities.2.name', catKey: 'Principal' },
{ image: "/assets/images/quinoa-sesamo.webp", alt: 'Quinoa plants in the Andean highlands and a sesame crop in flower', tagKey: 'Semillas', nameKey: 'commodities.9.name', catKey: 'Principal' }];


const commodityNames: Record<string, {es: string;en: string;}> = {
  'commodities.0.name': { es: 'Soja, Maíz Amarillo y Aceite de Soja', en: 'Soybeans, Yellow Corn & Soybean Oil' },
  'commodities.2.name': { es: 'Azúcares de Caña', en: 'Cane Sugars' },
  'commodities.4.name': { es: 'Café, Té Negro y Yerba Mate', en: 'Coffee, Black Tea & Yerba Mate' },
  'commodities.5.name': { es: 'Productos del Mar', en: 'Seafood' },
  'commodities.7.name': { es: 'Cordero Patagónico Argentino', en: 'Argentine Patagonian Lamb' },
  'commodities.8.name': { es: 'Carne Vacuna Argentina', en: 'Argentine Beef' },
  'commodities.9.name': { es: 'Quinoa y Sésamo', en: 'Quinoa & Sesame' }
};

const tagLabels: Record<string, {es: string;en: string;}> = {
  'Mar': { es: 'Mar', en: 'Seafood' },
  'Proteínas': { es: 'Proteínas', en: 'Proteins' },
  'Granos y Aceites': { es: 'Granos y Aceites', en: 'Grains & Oils' },
  'Infusiones': { es: 'Infusiones', en: 'Coffee & Teas' },
  'Azúcar': { es: 'Azúcar', en: 'Sugar' },
  'Semillas': { es: 'Granos y Semillas', en: 'Grains & Seeds' }
};

const catLabels: Record<string, {es: string;en: string;}> = {
  'Principal': { es: 'Principal', en: 'Core' },
  'A pedido': { es: 'A pedido', en: 'To order' }
};

const primaryBadge: Record<string, string> = { es: '★ PRINCIPAL', en: '★ CORE' };

export default function CommoditiesShowcase() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t, lang } = useLanguage();

  useEffect(() => {
    const reveals = sectionRef.current?.querySelectorAll('.reveal');
    if (!reveals) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {if (e.isIntersecting) e.target.classList.remove('hidden-init');}),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 bg-white overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="section-label block mb-3 reveal hidden-init">{t('commodities.label')}</span>
            <h2 className="font-display font-bold text-primary reveal hidden-init stagger-1" style={{ fontSize: 'clamp(32px, 4vw, 48px)', letterSpacing: '-0.025em', lineHeight: '1.08' }}>
              {t('commodities.title1')}
              <br />
              {t('commodities.title2')}{' '}
              <span className="italic font-light text-secondary">{t('commodities.titleItalic')}</span>
            </h2>
            <p className="text-brand-muted text-sm mt-3 reveal hidden-init stagger-2">{t('commodities.primaryLabel')}</p>
          </div>
          <Link href="/products" className="btn-primary-dark self-start md:self-end reveal hidden-init stagger-2">
            {t('commodities.fullPortfolio')}
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 reveal hidden-init">
          {/* Primary large card - Soja */}
          <div className="product-card md:col-span-5 h-80 md:h-[440px]">
            <AppImage src={commodities[0].image} alt={commodities[0].alt} fill className="object-cover" />
            <div className="overlay" />
            <div className="absolute inset-0 z-10 p-6 flex flex-col justify-end">
              <div className="flex items-center gap-2 mb-3">
                <span className="tag-badge self-start" style={{ background: 'rgba(26,122,138,0.2)', borderColor: 'rgba(26,122,138,0.4)', color: '#2A9BAD' }}>{tagLabels[commodities[0].tagKey][lang]}</span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(26,122,138,0.35)', color: '#fff', border: '1px solid rgba(26,122,138,0.5)' }}>{primaryBadge[lang]}</span>
              </div>
              <h3 className="font-display text-2xl font-bold text-white" style={{ letterSpacing: '-0.02em' }}>{commodityNames[commodities[0].nameKey][lang]}</h3>
              <p className="text-white/60 text-sm mt-1">{catLabels[commodities[0].catKey][lang]}</p>
            </div>
          </div>

          <div className="md:col-span-4 flex flex-col gap-5">
            {[commodities[1], commodities[2]].map((c) =>
            <div key={c.nameKey} className="product-card flex-1 h-52 md:h-auto">
                <AppImage src={c.image} alt={c.alt} fill className="object-cover" />
                <div className="overlay" />
                <div className="absolute inset-0 z-10 p-5 flex flex-col justify-end">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="tag-badge self-start" style={{ background: 'rgba(26,122,138,0.2)', borderColor: 'rgba(26,122,138,0.4)', color: '#2A9BAD' }}>{tagLabels[c.tagKey][lang]}</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(26,122,138,0.35)', color: '#fff', border: '1px solid rgba(26,122,138,0.5)' }}>{primaryBadge[lang]}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-white" style={{ letterSpacing: '-0.015em' }}>{commodityNames[c.nameKey][lang]}</h3>
                </div>
              </div>
            )}
          </div>

          <div className="md:col-span-3 flex flex-col gap-5">
            {[commodities[3], commodities[6], commodities[4], commodities[5]].map((c) =>
            <div key={c.nameKey} className="product-card flex-1 h-40 md:h-auto">
                <AppImage src={c.image} alt={c.alt} fill className="object-cover" />
                <div className="overlay" />
                <div className="absolute inset-0 z-10 p-4 flex flex-col justify-end">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-secondary-bright text-xs font-semibold uppercase tracking-wider">{tagLabels[c.tagKey][lang]}</span>
                    <span className="text-xs font-bold" style={{ color: '#2A9BAD' }}>★</span>
                  </div>
                  <h3 className="font-display text-base font-bold text-white" style={{ letterSpacing: '-0.01em' }}>{commodityNames[c.nameKey][lang]}</h3>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}